import React, { useState } from 'react';
import { CHART_PALETTE, SURFACE } from './chartColors';

interface RankedItem {
  label: string;
  value: number;
  subtitle?: string;
}

interface RankedListProps {
  data: RankedItem[];
  maxItems?: number;
  valueLabel?: string;
  colorIndex?: number;
  className?: string;
}

const RankedList: React.FC<RankedListProps> = ({
  data,
  maxItems = 10,
  valueLabel = '',
  colorIndex = 0,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const displayData = data.slice(0, maxItems);
  const maxValue = Math.max(...displayData.map(d => d.value), 1);
  const color = CHART_PALETTE[colorIndex % CHART_PALETTE.length];

  if (displayData.length === 0) {
    return <div className="flex items-center justify-center py-8 text-gray-400">No data</div>;
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      {displayData.map((item, i) => {
        const pct = (item.value / maxValue) * 100;
        const isHovered = hoveredIndex === i;
        const rankColor = i === 0 ? CHART_PALETTE[0] : i === 1 ? CHART_PALETTE[1] : i === 2 ? CHART_PALETTE[2] : null;

        return (
          <div
            key={i}
            className="relative rounded-lg cursor-pointer"
            style={{
              padding: '10px 14px',
              background: isHovered ? SURFACE.cardHover : 'transparent',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {/* Background bar */}
            <div
              className="absolute inset-y-0 left-0 rounded-r-md"
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${color.gradient[0]}12, ${color.gradient[1]}08)`,
                transition: 'width 0.3s ease',
              }}
            />

            <div className="relative flex items-center gap-3">
              {/* Rank number */}
              <div
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                style={{
                  background: rankColor
                    ? `linear-gradient(135deg, ${rankColor.gradient[0]}, ${rankColor.gradient[1]})`
                    : SURFACE.borderLight,
                  color: rankColor ? 'white' : SURFACE.textMuted,
                }}
              >
                {i + 1}
              </div>

              {/* Label */}
              <div className="flex-1 min-w-0">
                <div
                  className="text-sm truncate"
                  style={{
                    color: isHovered ? SURFACE.text : SURFACE.textSecondary,
                    fontWeight: isHovered ? 600 : 500,
                    transition: 'all 0.15s ease',
                  }}
                  title={item.label}
                >
                  {item.label}
                </div>
                {item.subtitle && (
                  <div className="text-xs truncate" style={{ color: SURFACE.textMuted }}>
                    {item.subtitle}
                  </div>
                )}
              </div>

              {/* Value */}
              <div
                className="flex-shrink-0 text-sm font-bold tabular-nums"
                style={{
                  color: isHovered ? color.dark : SURFACE.text,
                  transition: 'color 0.15s ease',
                }}
              >
                {item.value.toLocaleString()}
                {valueLabel && (
                  <span className="font-normal text-xs ml-1" style={{ color: SURFACE.textMuted }}>
                    {valueLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RankedList;
